import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  getPatientProfile,
  updatePatientProfile,
} from "@/api/userApi";
import { useAuth } from "@/auth/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import type { PatientProfile, PatientProfileUpdate } from "@/types/domain";

const BLOOD_GROUPS = [
  "",
  "A+",
  "A-",
  "B+",
  "B-",
  "AB+",
  "AB-",
  "O+",
  "O-",
] as const;

function buildUpdatePayload(form: Partial<PatientProfile>): PatientProfileUpdate {
  const payload: PatientProfileUpdate = {
    fullName: form.fullName?.trim() || undefined,
    dateOfBirth: form.dateOfBirth?.trim() || undefined,
    gender: form.gender || undefined,
    address: form.address?.trim() || undefined,
    profilePhoto: form.profilePhoto?.trim() || undefined,
    emergencyContact: form.emergencyContact?.trim() || undefined,
  };
  if (form.bloodGroup !== undefined && form.bloodGroup !== null) {
    payload.bloodGroup = form.bloodGroup;
  }
  if (form.phone !== undefined && form.phone !== null) {
    payload.phone = form.phone.trim();
  }
  return payload;
}

export default function PatientProfilePage() {
  const qc = useQueryClient();
  const { email: authEmail, username, fullName: authFullName } = useAuth();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["patient-profile"],
    queryFn: getPatientProfile,
  });

  const [form, setForm] = useState<Partial<PatientProfile>>({});
  const [saved, setSaved] = useState(false);

  /** Shown when the API has no name yet — Keycloak `name`, then email local-part, then username. */
  const authDerivedFullName = useMemo(() => {
    const n = authFullName?.trim();
    if (n) return n;
    const mail = authEmail?.trim();
    if (mail && mail.includes("@")) return mail.slice(0, mail.indexOf("@"));
    return username?.trim() ?? "";
  }, [authFullName, authEmail, username]);

  const displayEmail = authEmail?.trim() || username?.trim() || "";

  const fullNameDisplay =
    form.fullName ??
    (data
      ? (data.fullName?.trim() || authDerivedFullName)
      : authDerivedFullName);

  useEffect(() => {
    if (!data) return;
    const apiName = data.fullName?.trim() ?? "";
    setForm({
      ...data,
      bloodGroup: data.bloodGroup ?? undefined,
      fullName: apiName || authDerivedFullName,
    });
  }, [data, authDerivedFullName]);

  const save = useMutation({
    mutationFn: (body: PatientProfileUpdate) => updatePatientProfile(body),
    onSuccess: (d) => {
      qc.setQueryData(["patient-profile"], d);
      setSaved(true);
    },
  });

  const set = <K extends keyof PatientProfile>(
    k: K,
    v: PatientProfile[K] | undefined,
  ) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Box>
      <PageHeader title="My profile" subtitle="Keep your details up to date." />
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : "Could not load profile."}
        </Alert>
      )}
      {save.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {save.error instanceof Error
            ? save.error.message
            : "Could not save profile."}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Full name"
              value={fullNameDisplay}
              onChange={(e) => set("fullName", e.target.value)}
              disabled={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              value={displayEmail}
              disabled
              helperText="From your sign-in account; it cannot be edited here."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Mobile number"
              value={form.phone ?? ""}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="e.g. 9876543210"
              inputProps={{ inputMode: "tel", maxLength: 20 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Address"
              value={form.address ?? ""}
              onChange={(e) => set("address", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Date of birth"
              InputLabelProps={{ shrink: true }}
              value={form.dateOfBirth ?? ""}
              onChange={(e) => set("dateOfBirth", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Gender"
              value={form.gender ?? ""}
              onChange={(e) =>
                set("gender", (e.target.value || undefined) as PatientProfile["gender"])
              }
            >
              <MenuItem value="">Prefer not to say</MenuItem>
              <MenuItem value="FEMALE">Female</MenuItem>
              <MenuItem value="MALE">Male</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Blood group"
              value={form.bloodGroup ?? ""}
              onChange={(e) => set("bloodGroup", e.target.value)}
            >
              {BLOOD_GROUPS.map((bg) => (
                <MenuItem key={bg || "unset"} value={bg}>
                  {bg === "" ? "Prefer not to say" : bg}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            variant="contained"
            disabled={save.isPending || isLoading}
            onClick={() => save.mutate(buildUpdatePayload(form))}
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </Stack>
      </Paper>

      <Button component={RouterLink} to="/delete-account" color="error" sx={{ mt: 3 }}>
        Delete my account and data
      </Button>

      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={() => setSaved(false)}
      >
        <Alert severity="success" variant="filled">
          Profile updated
        </Alert>
      </Snackbar>
    </Box>
  );
}
